<template>
  <v-container fluid >
    <v-dialog v-model="showAddSubDialog" max-width="600px">
      <v-card>
        <v-card-title>Link User as Sub Account</v-card-title>
        <v-card-text>
          <CustomerAutocomplete v-model="userToAdd"/>
        </v-card-text>
        <v-card-actions>
          <v-btn @click="showAddSubDialog=false">Cancel</v-btn>
          <v-btn @click="addSubAccount(userToAdd)" :loading="loading">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showAddManagedAccountDialog" max-width="600px">
      <v-card>
        <v-card-title>Add Existing Customer as Managed Account</v-card-title>
        <v-card-text>
          <CustomerAutocomplete v-model="userToAdd"/>
        </v-card-text>
        <v-card-actions>
          <v-btn @click="showAddManagedAccountDialog=false">Cancel</v-btn>
          <v-btn @click="updateManagedAccount(userToAdd.uid, true); showAddManagedAccountDialog=false"
                 :loading="loading">Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showSaveConfirmDialog" max-width="800px" @click:outside="cancelSave">
      <v-card>
        <v-card-title>Confirm changes</v-card-title>
        <v-card-text>
          <p v-if="pendingChanges.length === 0">No changes detected.</p>
          <v-list dense v-else>
            <v-list-item v-for="(change, i) in pendingChanges" :key="i">
              <v-list-item-content>
                <v-list-item-title>
                  <v-chip x-small outlined :color="changeTypeColor(change.type)" class="mr-2">{{ change.type }}</v-chip>
                  {{ change.path.join('.') }}
                </v-list-item-title>
                <v-list-item-subtitle class="mt-1">
                  <span v-if="change.oldValue !== undefined" class="text--secondary">was: {{ formatChangeValue(change.oldValue) }}</span>
                  <span v-if="change.oldValue !== undefined && change.value !== undefined"> &rarr; </span>
                  <span v-if="change.value !== undefined">now: {{ formatChangeValue(change.value) }}</span>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="cancelSave">Cancel</v-btn>
          <v-spacer/>
          <v-btn :loading="saving" color="primary" :disabled="pendingChanges.length === 0" @click="performSaveCustomer">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-alert :value="error">{{ error }}</v-alert>
    <v-card flat v-if="!loading && !error" class="">
      <v-card-actions class="py-0" v-if="tab !== 4">
        <v-btn text outlined @click="reloadCustomer">Reload</v-btn>
        <v-spacer/>
        <v-btn v-if="canDeleteUser" color="red" text outlined @click="deleteCustomer">Delete</v-btn>
        <v-spacer/>
        <v-btn :text="!hasUnsavedChanges" :outlined="!hasUnsavedChanges" :color="hasUnsavedChanges ? 'warning' : ''" :loading="saving" @click="saveCustomer">Save</v-btn>
        <v-spacer/>
        <CustomerAutocomplete
          class="text-right"
          :value="customer"
          v-on:input="$router.push({params:{id: $event.uid}})"
        />
      </v-card-actions>
      <v-card-title class="pt-0">
        <CustomerHeader :customer="customer" :show-link="false"/>

        <v-spacer/>
        <v-chip outlined v-if="createdDate">Signup: {{ createdDate }}</v-chip>
        <template v-if="customer.confirmed===false || customer.confirmed===true">
          <v-spacer/>
          <v-chip v-if="customer.confirmed===true" outlined color="green">Confirmed</v-chip>
          <v-chip v-if="customer.confirmed===false" outlined color="error">Not Confirmed</v-chip>
        </template>
        <v-spacer/>
        <v-chip class="mr-2" outlined color="green" v-if="customer.isValidCard">Valid CC</v-chip>
        <v-chip outlined color="error" v-if="!(customer.isValidCard || customer.mainAccount)">Not Valid CC</v-chip>
        <v-chip @click="tab=5" outlined color="purple" v-if="customer.subAccounts">Main Account</v-chip>
        <template v-if="customer.mainAccount">
          <router-link :to="{params:{id:customer.mainAccount}}">
            <v-chip outlined color="purple">Sub Account</v-chip>
          </router-link>
        </template>
      </v-card-title>
      <v-toolbar flat dense>

        <v-btn outlined target="_blank" :href="`https://personalized.fedfedfed.com/?uid=${id}&fed-ui=personalized`">
          <v-icon left color="blue">mdi-incognito</v-icon>
          V3 (p.fed)
        </v-btn>
        <v-btn outlined target="_blank" :href="`https://app.fedfedfed.com/?uid=${id}&fed-ui=v2`" class="ml-2">
          <v-icon left color="red">mdi-incognito</v-icon>
          V4 (app.fed)
        </v-btn>
        <!-- the website THIS customer uses (users/<uid>.ui_version) -->
        <v-btn outlined target="_blank" class="ml-2" :disabled="!customerSite"
               :href="customerSite ? customerSite.href : undefined"
               :title="customerSite ? `Open as this customer on the site they use: ${customerSite.label}` : 'No site recorded for this customer yet'">
          <v-icon left color="green">mdi-incognito</v-icon>
            Uses {{ customerSite ? customerSite.label : '?'}}
        </v-btn>
        <v-spacer/>

        <router-link :to="{name: 'OrderEdit', query: {customer: id, dates: [prevOrderDate]}}" class="ml-2">
          <v-btn color="primary" outlined>
            <v-icon left>mdi-pencil</v-icon>
            {{ dateFormatted(prevOrderDate, {formatString: 'MMM DD'}) }}
          </v-btn>
        </router-link>
        <router-link :to="{name: 'OrderEdit', query: {customer: id, dates: [orderDate]}}" class="ml-2">
          <v-btn color="primary" outlined>
            <v-icon left>mdi-pencil</v-icon>
            {{ dateFormatted(orderDate, {formatString: 'MMM DD'}) }}
          </v-btn>
        </router-link>
        <v-spacer/>
        <router-link v-if="customer.stripe || (customer.beforeDelete && customer.beforeDelete.stripe_id)"
                     :to="{name: 'StripeCustomer', params: {uid: id}}" class="ml-2">
          <v-btn outlined>
            <v-icon left>mdi-credit-card-outline</v-icon>
            Stripe
          </v-btn>
        </router-link>

        <router-link :to="{name:'CustomerInvoiceComparison', query: { uid: id, weekDate: prevOrderDate}}" class="ml-2">
          <v-btn outlined>
            <v-icon left>mdi-compare</v-icon>
            {{ dateFormatted(prevOrderDate, {formatString: 'MMM DD'}) }}
          </v-btn>
        </router-link>
        <router-link :to="{name:'CustomerInvoiceComparison', query: { uid: id, weekDate: orderDate}}" class="ml-2">
          <v-btn outlined>
            <v-icon left>mdi-compare</v-icon>
            {{ dateFormatted(orderDate, {formatString: 'MMM DD'}) }}
          </v-btn>
        </router-link>

        <v-spacer/>
        <router-link :to="{name: 'CustomerEmailLog', query: {email: customer.email}}" class="ml-2">
          <v-btn color="primary" outlined>
            <v-icon left>mdi-email</v-icon>
            Emails
          </v-btn>
        </router-link>
        <router-link :to="{name: 'CustomerTracking', params: {uid: id}}" class="ml-2">
          <v-btn color="primary" outlined>
            <v-icon left>mdi-chart-timeline</v-icon>
            History
          </v-btn>
        </router-link>
      </v-toolbar>
      <v-alert
        v-if="csGeneralNote && csGeneralNote.note"
        outlined
        type="info"
        class="mx-4 mt-2 mb-0"
        @click="tab = 4"
        style="cursor: pointer; white-space: pre-wrap"
      >
        <strong>Staff note:</strong> {{ csGeneralNote.note }}
      </v-alert>
      <v-card-text>
        <v-tabs v-model="tab">
          <v-tab key="detail">Customer Detail</v-tab>
          <v-tab key="address">Addresses</v-tab>
          <v-tab key="delivery">Delivery</v-tab>
          <v-tab key="notes">Pack Notes</v-tab>
          <v-tab key="csNotes">CS Notes</v-tab>
          <v-tab key="subaccounts">Subaccounts</v-tab>
          <v-tab key="managedAccounts">Managed Accounts</v-tab>
          <v-tab key="donateDates">Donate Shipments</v-tab>
          <v-tab key="coupons">Coupons</v-tab>
          <v-tab key="creditRefund">Credit / Refund</v-tab>
        </v-tabs>
        <v-tabs-items v-model="tab">
          <v-tab-item key="detail">
            <v-card flat>
              <v-card-title></v-card-title>
              <v-card-text>
                <v-row dense>
                  <v-col cols="4" v-for="(field,i) of fields" v-bind:key="`${i}-${field}`">
                    <v-checkbox
                      dense
                      v-if="isCheckbox(field)"
                      :disabled="disabled[getProperty(field)]"
                      :label="getProperty(field)"
                      v-model="customer[getProperty(field)]"
                    />
                    <allergy-select
                      v-if="isAllergyField(field)"
                      :disabled="disabled[getProperty(field)]"
                      :label="getProperty(field)"
                      v-model="customer[getProperty(field)]"
                      :settings="customer[allergySettingsProperty(field)]"
                      @update:settings="val => $set(customer, allergySettingsProperty(field), val)"
                    />
                    <!-- the diet pills' stream preference (menu only from / leaves out), read-only: it is
                         set by the customer's pills, edited here only through the raw JSON if ever -->
                    <div v-if="isAllergyField(field) && streamPreferenceText(field)" class="text-caption grey--text text--darken-1 mt-n2 mb-2">
                      Personal menu: {{ streamPreferenceText(field) }}
                    </div>
                    <!--            {{ {v: getValue(field), t: getText(field), o: selectOptions(field)} }}-->
                    <v-select
                      v-if="isSelect(field)"
                      :disabled="disabled[getProperty(field)]"
                      :label="getProperty(field)"
                      :items="selectOptions(field)"
                      v-model="customer[getProperty(field)]"
                      :multiple="isMultiple(field)"
                      :clearable="isMultiple(field)"
                    />
                    <v-text-field
                      v-if="isCurrency(field)"
                      :disabled="disabled[getProperty(field)]"
                      :label="`${getProperty(field)} (in cents)`"
                      type="number"
                      v-model="customer[getProperty(field)]"
                    />
                    <v-text-field
                      v-if="isTextField(field)"
                      :disabled="disabled[getProperty(field)]"
                      :label="getProperty(field)"
                      v-model.trim="customer[getProperty(field)]"
                    />
                    <v-alert
                      v-if="hasPendingEdit(field)"
                      dense
                      type="warning"
                      outlined
                      class="mt-1"
                    >
                      <small>Pending: {{ formatPendingEdit(field) }}</small>
                    </v-alert>
                  </v-col>
                </v-row>

              </v-card-text>
              <v-card-actions>
                <v-btn text outlined @click="changeEmailPrompt">Change Email Address</v-btn>
                <v-btn text outlined v-if="canEditPassword" @click="changePasswordPrompt">Change Password</v-btn>
                <v-spacer/>
              </v-card-actions>
            </v-card>
          </v-tab-item>
          <v-tab-item key="address">
            <v-card flat>
              <v-tabs v-model="addressTab">
                <v-tab>Primary Address</v-tab>
                <v-tab v-for="(address,index) of addressList" v-bind:key="`${index}-address`">
                  {{ address }}
                </v-tab>
                <v-spacer/>
                <!--                {{ addresses }}-->
                <v-btn outlined @click="addNewAddress">Add Another Address</v-btn>
              </v-tabs>
              <v-spacer/>


              <v-card-text>
                <b>
                  Note: City is shown twice below. If you enter a city not in our standard rates, the dropdown will be
                  blank and the default shipping charge will be used ($9.99)
                </b>
                <v-row>
                  <v-col cols="3" v-for="(field,i) of addressFields" v-bind:key="`${i}-${field}`">
                    <template v-if="field!=='blank'">
                      <v-select
                        v-if="isSelect(field)"
                        :disabled="disabled[getProperty(field)]"
                        :label="getProperty(field)"
                        :items="selectOptions(field)"
                        v-model="address[getProperty(field)]"
                        :multiple="isMultiple(field)"
                      />
                      <v-checkbox
                        dense
                        v-if="isCheckbox(field)"
                        :disabled="disabled[getProperty(field)]"
                        :label="getProperty(field)"
                        v-model="address[getProperty(field)]"
                      />
                      <v-text-field v-if="isTextField(field)"
                                    :label="field"
                                    :disabled="disabled[field]"
                                    v-model.trim="address[field]"
                      />
                      <v-text-field
                        v-if="isTime(field)"
                        :disabled="disabled[getProperty(field)]"
                        :label="`${getProperty(field)} (24 hour clock)`"
                        type="time"
                        v-model="address[getProperty(field)]"
                      />
                      <v-alert
                        v-if="hasAddressPendingEdit(field)"
                        dense
                        type="warning"
                        outlined
                        class="mt-1"
                      >
                        <small>Pending: {{ formatAddressPendingEdit(field) }}</small>
                      </v-alert>
                    </template>
                  </v-col>
                  <v-col cols="12">
                    <v-textarea
                      rows="1"
                      auto-grow
                      label="delivery_instruction"
                      v-model="address['delivery_instruction']"
                    />
                    <v-alert
                      v-if="hasAddressPendingEdit('delivery_instruction')"
                      dense
                      type="warning"
                      outlined
                      class="mt-1"
                    >
                      <small>Pending: {{ formatAddressPendingEdit('delivery_instruction') }}</small>
                    </v-alert>
                  </v-col>
                </v-row>
                <v-alert
                  v-if="coordinatesState(address) === 'current'"
                  type="info" outlined dense
                >
                  Onfleet could not geocode this address, so deliveries use saved coordinates
                  <a :href="mapsUrl(address.coordinates)" target="_blank" rel="noopener">
                    {{ address.coordinates.lat }}, {{ address.coordinates.lng }} (open in Google Maps)
                  </a>.
                  <small v-if="address.coordinates.formattedAddress">
                    Google matched: {{ address.coordinates.formattedAddress }}
                  </small>
                </v-alert>
                <v-alert
                  v-if="coordinatesState(address) === 'stale'"
                  type="warning" outlined dense
                >
                  This address has saved coordinates from before it was edited; they are no longer used.
                  If Onfleet cannot geocode the new address, find it on the map on the Shipments page.
                </v-alert>
                <v-alert
                  v-if="customer.plan==='flex' && !address.use_shipping"
                  type="warning" outlined
                >
                  Warning: if plan is 'flex' then 'use_shipping' must be checked!
                  <br/>
                  Also, make sure you "clear all" on the customer's order when changing to (or from) shipping to
                  local
                  delivery
                </v-alert>
                <v-alert
                  v-if="customer.plan!=='flex' && address.use_shipping"
                  type="warning" outlined
                >
                  Warning: if plan is not 'flex' then 'use_shipping' must be unchecked
                </v-alert>
              </v-card-text>

            </v-card>
          </v-tab-item>
          <v-tab-item key="delivery">
            <v-card-title>Delivery Address Overrides</v-card-title>
            <v-card-text>
              If an alternative delivery address is required for a particular delivery, it may be
              set here. If left blank, the customer's primary address will be used.
              <br/>
              <br/>
              Customer primary address is :
              {{ formatAddressAndDeliveryTimes(customer.address || {}) }}


              <template v-if="customer && customer.useAddress">
                {{ customer.useAddress.mon_tue_wed }}
                {{ customer.useAddress.thu_fri_sat }}
              </template>
            </v-card-text>
            <template v-if="customer.useAddress">
              <v-card-actions>
                <v-select
                  label="Mon/Tue/Wed Meals"
                  v-model="customer.useAddress.mon_tue_wed"
                  :items="addresses"
                  :item-text="a => `${formatAddressAndDeliveryTimes(a || customer.address)}`"
                  return-object
                  clearable
                />
                <v-spacer/>
                <v-select
                  label="Thu/Fri/Sat Meals"
                  v-model="customer.useAddress.thu_fri_sat"
                  :items="addresses"
                  :item-text="a => `${formatAddressAndDeliveryTimes(a || customer.address)}`"
                  return-object
                  clearable
                />
              </v-card-actions>
            </template>
          </v-tab-item>
          <v-tab-item key="notes">
            <v-card flat>
              <v-card-title>
                Packing Notes
                <v-spacer/>
                <v-btn @click="addNote">Add Note</v-btn>
              </v-card-title>
              <v-card-text>
                <v-alert dense outlined type="info" class="mb-3">
                  These notes appear on packing sheets.
                </v-alert>
                <v-textarea
                  label="General Comment (appears all the time)"
                  rows="2"
                  auto-grow
                  v-model="customer.comment"
                  class="my-4"
                />
                <v-row v-for="(note,i) of customer.customer_notes" v-bind:key="`${i}-note`">
                  <v-col>
                    <v-combobox
                      label="Start"
                      v-model="note.start"
                      :items="upcomingDeliveryDates"
                      clearable
                      v-on:change="note.end = note.end >= note.start ? note.end : note.start"
                    />
                  </v-col>
                  <v-col>
                    <v-combobox
                      label="End"
                      v-model="note.end"
                      :items="upcomingDeliveryDates"
                      clearable
                      v-on:change="note.start = note.end >= note.start ? note.start : note.end"
                    />
                  </v-col>
                  <v-col>
                    <v-text-field
                      label="Note"
                      v-model="note.text"
                    />
                  </v-col>
                  <v-col>
                    <v-btn icon @click="customer.customer_notes.splice(i,1)">
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-tab-item>
          <v-tab-item key="csNotes">
            <v-card flat>
              <v-card-title>General Staff Note</v-card-title>
              <v-card-text>
                <v-alert dense outlined type="info" class="mb-3">
                  Persistent staff notes that don't go on packing slips (e.g. "prefers phone over email").
                </v-alert>
                <v-textarea
                  v-model="csGeneralNoteDraft"
                  rows="3"
                  auto-grow
                  outlined
                  hide-details
                  placeholder="Add a general staff note..."
                />
                <div v-if="csGeneralNote && csGeneralNote.updated_at" class="text-caption mt-1 text--secondary">
                  Last updated {{ dateFormatted(csGeneralNote.updated_at, {formatString: 'LLL'}) }}
                  <template v-if="csGeneralNote.updated_by_email">by {{ csGeneralNote.updated_by_email }}</template>
                </div>
              </v-card-text>
              <v-card-actions>
                <v-spacer/>
                <v-btn
                  outlined
                  :loading="csGeneralNoteSaving"
                  :disabled="!csGeneralNoteDirty"
                  @click="saveCsGeneralNote"
                >
                  Save Note
                </v-btn>
              </v-card-actions>

              <v-divider class="my-3"/>

              <v-card-title>
                Customer Interaction History
                <v-spacer/>
              </v-card-title>
              <v-card-text>
                <v-row align="start">
                  <v-col cols="3">
                    <v-select
                      label="Type"
                      :items="interactionTypes"
                      v-model="newInteraction.type"
                      outlined
                      dense
                      hide-details
                    >
                      <template v-slot:item="{item}">
                        <v-icon left small :color="item.color">{{ item.icon }}</v-icon>
                        {{ item.text }}
                      </template>
                      <template v-slot:selection="{item}">
                        <v-icon left small :color="item.color">{{ item.icon }}</v-icon>
                        {{ item.text }}
                      </template>
                    </v-select>
                    <div v-if="user && user.email" class="text-caption mt-1 text--secondary">
                      Recording as {{ user.email }}
                    </div>
                  </v-col>
                  <v-col>
                    <v-textarea
                      label="Comment"
                      v-model="newInteraction.comment"
                      rows="1"
                      auto-grow
                      outlined
                      dense
                      hide-details
                    />
                  </v-col>
                  <v-col cols="2" class="text-right">
                    <v-btn
                      color="primary"
                      outlined
                      :loading="addingInteraction"
                      :disabled="!newInteraction.type || !(newInteraction.comment||'').trim()"
                      @click="addInteraction"
                    >
                      Save Comment
                    </v-btn>
                  </v-col>
                </v-row>

                <v-simple-table v-if="interactions.length" class="mt-3" style="width: 100%; table-layout: fixed">
                  <thead>
                  <tr>
                    <th style="width: 140px">When</th>
                    <th style="width: 180px">Staff</th>
                    <th style="width: 100px">Type</th>
                    <th>Comment</th>
                    <th style="width: 56px"></th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="i of interactions" v-bind:key="i.id">
                    <td>{{ dateFormatted(i.inserted_at, {formatString: 'YYYY-MM-DD HH:mm'}) }}</td>
                    <td style="overflow: hidden; text-overflow: ellipsis">{{ i.created_by_email || '—' }}</td>
                    <td>
                      <v-chip small :color="interactionType(i.type).color" dark>
                        <v-icon left x-small>{{ interactionType(i.type).icon }}</v-icon>
                        {{ interactionType(i.type).text }}
                      </v-chip>
                    </td>
                    <td style="white-space: pre-wrap; word-break: break-word">{{ i.comment }}</td>
                    <td class="text-right">
                      <v-btn v-if="canDeleteUser" icon small @click="deleteInteraction(i.id)">
                        <v-icon small color="red">mdi-delete</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                  </tbody>
                </v-simple-table>
                <div v-else class="text--secondary mt-3">No interactions logged yet.</div>
              </v-card-text>
            </v-card>
          </v-tab-item>
          <v-tab-item key="subaccounts">
            <v-card flat v-if="customer.mainAccount">
              <v-card-text>
                This account is a sub-account and you cannot create a subaccount of a subaccount!
                <br/>
                Go to the
                <router-link :to="{params:{id:customer.mainAccount}}">Main Account</router-link>
              </v-card-text>
            </v-card>
            <v-card flat v-if="!customer.mainAccount">
              <v-card-title>
                SubAccounts
                <v-spacer/>
                <v-btn @click="showAddSubDialog=true" text outlined v-if="canEditSubaccounts">
                  Link a sub-account
                  <v-icon color="green">mdi-plus</v-icon>
                </v-btn>
              </v-card-title>
              <v-alert v-if="canEditSubaccounts && !isAccountChangeWindowOpen()" type="warning" dense outlined class="mx-4">
                Sub-account / main-account changes can only be made between Sunday and Wednesday at noon (America/Vancouver).
                Outside this window, any Stripe payments already made for the upcoming week (e.g. a new signup) will need to be fixed manually.
              </v-alert>
              <v-card-text v-if="!customer.mainAccount">
                <!-- one card per delivery, grouped as the Shipments page groups them -->
                <v-card outlined class="mb-3" v-for="group of subAccountGroups" v-bind:key="group.address">
                  <v-card-subtitle class="pb-1 font-weight-bold">
                    {{ group.address }}
                    <span v-if="group.members.length > 1" class="font-weight-regular">
                      &middot; {{ group.members.length }} accounts, one delivery
                    </span>
                  </v-card-subtitle>
                  <v-card-text>
                    <v-row dense align="center" v-for="member of group.members" v-bind:key="`${member.uid}-${member.note}`">
                      <v-col>
                        <span v-if="!member.account">Customer not found ({{ member.uid }})</span>
                        <CustomerHeader v-else :customer="member.account" :show-link="!member.isMain"/>
                        <v-chip x-small class="ml-2" v-if="member.isMain">this account</v-chip>
                        <v-chip x-small class="ml-2" v-if="member.note"
                                :color="member.pending ? 'warning' : ''" :outlined="!member.pending">
                          {{ member.note }}
                        </v-chip>
                      </v-col>
                      <v-col cols="1" class="text-right" v-if="canEditSubaccounts && !member.isMain">
                        <v-btn small text color="red" @click="confirmUnlinkSub(member.uid)">Unlink</v-btn>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-card-text>
            </v-card>
          </v-tab-item>
          <v-tab-item key="managedAccounts">
            <v-card flat v-if="!customer.is_account_manager">
              <v-card-text>
                This account is not an account manager.
              </v-card-text>
            </v-card>
            <v-card flat v-if="customer.is_account_manager">
              <v-card-title>
                Managed Accounts (read access and commission)
                <v-spacer/>
                <v-btn @click="showAddManagedAccountDialog=true" text outlined v-if="canEditCommission">
                  Add Existing Account
                  <v-icon color="green">mdi-plus</v-icon>
                </v-btn>
              </v-card-title>
              <v-card-text class="text-right">
                <v-row dense v-for="(managedAccount,uid) of (customer.accountsManaged||{})"
                       v-bind:key="`${uid}-managed`">
                  <template v-if="managedAccount.allowed">
                    <template v-if="!subAccounts[uid]">
                      <v-col>Customer not found</v-col>
                    </template>
                    <template v-if="subAccounts[uid]">
                      <v-col>
                        <CustomerHeader :customer="subAccounts[uid]"/>
                      </v-col>
                      <v-col>
                        commission earned {{ formatCurrency(managedAccount.totalCommission || 0) }}
                      </v-col>
                    </template>
                    <v-col cols="1" class="text-right" v-if="canEditCommission">
                      <v-btn icon @click="confirmRemoveManagedAccount(uid)">
                        <v-icon color="red">mdi-trash-can</v-icon>
                      </v-btn>
                    </v-col>
                  </template>
                </v-row>
              </v-card-text>
            </v-card>
          </v-tab-item>
          <v-tab-item key="donateDates">
            <v-card flat>
              <v-card-title>
                Donate Shipments
                <v-spacer/>
                <v-combobox
                  v-model="newDonateDate"
                  :items="upcomingDeliveryDates"
                  label="Add shipment date"
                  dense
                  outlined
                  hide-details
                  style="max-width:200px"
                  class="mr-2"
                />
                <v-btn outlined @click="addDonateDate" :disabled="!newDonateDate">
                  <v-icon left>mdi-plus</v-icon>
                  Add
                </v-btn>
              </v-card-title>
              <v-card-text>
                <v-alert type="info" outlined dense class="mb-3">
                  Note: office deliveries (Mondays) will be filtered out if the corresponding Sunday date is selected.
                </v-alert>
                <span v-if="!customer.donateDates || !customer.donateDates.length" class="text--secondary">
                  No donate shipment dates set.
                </span>
                <v-chip
                  v-for="(date, i) in (customer.donateDates || [])"
                  :key="date"
                  close
                  outlined
                  class="mr-2 mb-2"
                  @click:close="customer.donateDates.splice(i, 1)"
                >
                  {{ date }}
                </v-chip>
              </v-card-text>
            </v-card>
          </v-tab-item>
          <v-tab-item key="coupons">
            <!-- the customer's Stripe coupons; applied straight to Stripe, nothing to save here -->
            <CustomerCoupons v-if="tab === 8" :uid="id" :customer-name="`${customer.first_name || ''} ${customer.last_name || ''}`.trim()"
                             :can-edit-coupons-and-credit="canEditCouponsAndCredit" @coupon-applied="fetchCsNotes(id)"/>
          </v-tab-item>
          <v-tab-item key="creditRefund">
            <!-- their Stripe credit and refunds; also straight to Stripe -->
            <CustomerCreditRefund v-if="tab === 9" :uid="id" :can-edit-coupons-and-credit="canEditCouponsAndCredit"
                                  @changed="fetchCsNotes(id)"/>
          </v-tab-item>
        </v-tabs-items>
        <v-card-actions v-if="tab !== 4 && tab !== 8 && tab !== 9">
          <v-btn text outlined @click="reloadCustomer">Reload</v-btn>
          <v-spacer/>
          <v-btn v-if="canDeleteUser" color="red" text outlined @click="deleteCustomer">Delete</v-btn>
          <v-spacer/>
          <v-btn :text="!hasUnsavedChanges" :outlined="!hasUnsavedChanges" :color="hasUnsavedChanges ? 'warning' : ''" :loading="saving" @click="saveCustomer">Save</v-btn>
        </v-card-actions>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import api from '@/api';
import {coordinatesState, mapsUrl} from '@/utils/address-coordinates';
import {mapActions, mapState} from 'vuex';
import CustomerAutocomplete from "@/components/CustomerAutocomplete";
import CustomerHeader from "@/components/CustomerHeader";
import CustomerCoupons from "@/components/CustomerCoupons";
import CustomerCreditRefund from "@/components/CustomerCreditRefund";
import AllergySelect from "@/components/AllergySelect";
import urlState from "@/router/urlState";
import {
  deepCopy,
  formatAddress,
  formatCurrency,
  formatDeliveryTimes,
  getNextOrderingDate,
  groupOrdersByDelivery,
  normalizeName
} from "@/store/utils";
import diff from "microdiff";
import moment from "moment";
import 'moment-timezone';

import {dateFormatted, formatDate} from "../store/utils";

function isDeepEmpty(v) {
  if (v === undefined || v === null) return true;
  if (typeof v === 'string') return v === '';
  if (Array.isArray(v)) return v.every(isDeepEmpty);
  if (typeof v === 'object') return Object.values(v).every(isDeepEmpty);
  return false;
}

function isNoOpChange(change) {
  // microdiff emits CREATE entries when a key was absent on one side; form initialization
  // routinely adds empty/default-shaped values. Treat anything that's empty all the way
  // down (e.g. {nutrient_preferences: []}) as a non-change.
  if (change.type === 'CREATE' && isDeepEmpty(change.value)) return true;
  // address.delivery_instruction is auto-populated from customer.delivery_instruction at
  // load time when missing on the address. Suppress the resulting null→value diff.
  if (
    change.path.length === 2
    && change.path[0] === 'address'
    && change.path[1] === 'delivery_instruction'
    && change.type === 'CREATE'
  ) {
    return true;
  }
  return false;
}

function saveFailedMessage(changesSinceLoad) {
  return `
SAVE FAILED!!!  :'(

This customer was modified since you loaded this page.  Please hit RELOAD and make your changes again.

${JSON.stringify(changesSinceLoad)}
`;
}

function dotPathToObject(customer) {
  Object.keys(customer)
    .forEach(key => {
      const valueToSet = customer[key];
      if (valueToSet !== undefined) {
        const path = key.split('.');
        if (path.length > 1) {
          let objectToSet = customer;
          while (path.length > 1) {
            const prop = path.shift();
            if (objectToSet[prop] === undefined) {
              objectToSet[prop] = {};
            }
            objectToSet = objectToSet[prop];
          }
          objectToSet[path[0]] = valueToSet;
          delete customer[key];
        }

      }
    })
}

export default {
  name: "CustomerDetail",
  components: {CustomerAutocomplete, CustomerHeader, CustomerCoupons, CustomerCreditRefund, AllergySelect},
  mixins: [urlState],
  props: {
    id: {type: String},
    canEditSubaccounts: {
      type: Boolean,
      default: false,
      required: false
    },
    canEditPassword: {
      type: Boolean,
      default: false,
      required: false
    },
    canEditPlan: {
      type: Boolean,
      default: false,
      required: false
    },
    canEditLoginDisabled: {
      type: Boolean,
      default: false,
      required: false
    },
    canDeleteUser: {
      type: Boolean,
      default: false,
      required: false
    },
    canChangeAdmin: {
      type: Boolean,
      default: false,
      required: false
    },
    canEditCommission: {
      type: Boolean,
      default: false,
      required: false
    },
    // the buttons on the Coupons and Credit / Refund tabs: apply/remove a coupon, adjust the credit balance, refund
    canEditCouponsAndCredit: {
      type: Boolean,
      default: false,
      required: false
    },
  },
  mounted() {
    window.addEventListener('beforeunload', this.handleBeforeUnload);
    // the first tab is the default and stays out of the url: the tab bars select it by
    // themselves when the url names none, and writing that to the url pushed a history
    // entry each time, so the back button landed straight back on ?address=0
    this.syncToUrl({
      param: 'tab',
      urlParam: 'tab',
      initFromRoute: true,
      parseCallback: (v) => v && Number(v),
      transformCallback: (v) => v || undefined
    });
    this.syncToUrl({
      param: 'addressTab',
      urlParam: 'address',
      initFromRoute: true,
      parseCallback: (v) => v && Number(v),
      transformCallback: (v) => v || undefined
    });


    return Promise.all([
      this.fetchPlans(),
      this.fetchShipping(),
      this.fetchRestrictions(),
      this.fetchStreams(),
      this.fetchCustomer(this.id),
      this.fetchMealFilters()
    ]);
    // this.fetchCities()
    //     .then(r => {
    //       console.log('cities', r);
    //     });
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
  },
  watch: {
    addressTab(index) {
      this.setAddress(index);
    }
  },
  data() {
    return {
      disabled: {
        // city: true,
        plan: !this.canEditPlan,
        login_disabled: !this.canEditLoginDisabled,
        free_shipping: !this.canEditPlan,
        show_meals: !this.canEditPlan,
        allow_allergy_swaps: !this.canEditPlan,
      },
      loading: null,
      saving: false,
      customer: {},
      customerInitialValues: {},
      address: {},
      error: null,
      subAccounts: {},
      showAddSubDialog: null,
      userToAdd: null,
      tab: 0,
      useAddress: null,
      addresses: null,
      addressTab: 0,
      isSecondary: null,
      availableFilters: null,
      availableFilterNames: null,
      showAddManagedAccountDialog: null,
      showSaveConfirmDialog: false,
      pendingChanges: [],
      newDonateDate: null,
      interactions: [],
      csGeneralNote: null,
      csGeneralNoteDraft: '',
      csGeneralNoteSaving: false,
      newInteraction: {type: 'phone', comment: ''},
      addingInteraction: false,
      interactionTypes: [
        {text: 'Phone',         value: 'phone',          icon: 'mdi-phone',        color: 'blue'},
        {text: 'Email',         value: 'email',          icon: 'mdi-email',        color: 'teal'},
        {text: 'SMS',           value: 'sms',            icon: 'mdi-message-text', color: 'purple'},
        {text: 'In Person',     value: 'in_person',      icon: 'mdi-account',      color: 'green'},
        {text: 'Credit Issued', value: 'credit_issued',  icon: 'mdi-cash-plus',    color: 'orange'},
        {text: 'Refund Issued', value: 'refund_issued',  icon: 'mdi-cash-refund',  color: 'red'},
        {text: 'Other',         value: 'other',          icon: 'mdi-dots-horizontal', color: 'grey'},
      ],
    }
  },
  methods: {
    formatDate,
    formatCurrency,
    ...mapActions(['fetchPlans', 'fetchShipping', 'fetchRestrictions', 'fetchStreams']),
    formatAddress,
    formatDeliveryTimes,
    formatAddressAndDeliveryTimes(a) {
      return `${formatAddress(a)} (${formatDeliveryTimes(a)})`;
    },
    interactionType(type) {
      return this.interactionTypes.find(t => t.value === type) || {text: type, icon: 'mdi-dots-horizontal', color: 'grey'};
    },
    formatInteractionType(type) {
      return this.interactionType(type).text;
    },
    async fetchCsNotes(uid) {
      try {
        const {data} = await api.get(`v2/customer/${uid}/cs-notes`);
        this.interactions = data.interactions || [];
        this.csGeneralNote = data.generalNote || null;
        this.csGeneralNoteDraft = (data.generalNote && data.generalNote.note) || '';
      } catch (e) {
        console.warn('failed to fetch cs notes', e);
      }
    },
    async saveCsGeneralNote() {
      this.csGeneralNoteSaving = true;
      try {
        const {data} = await api.put(`v2/customer/${this.id}/cs-notes/general`, {
          note: this.csGeneralNoteDraft || ''
        });
        this.csGeneralNote = data;
      } catch (e) {
        alert('failed to save staff note: ' + e.message);
      } finally {
        this.csGeneralNoteSaving = false;
      }
    },
    async addInteraction() {
      const {type, comment} = this.newInteraction;
      if (!type || !(comment || '').trim()) return;
      this.addingInteraction = true;
      try {
        const {data} = await api.post(`v2/customer/${this.id}/cs-notes/interactions`, {
          type,
          comment: comment.trim(),
        });
        this.interactions.unshift(data);
        this.newInteraction = {type: 'phone', comment: ''};
      } catch (e) {
        alert('failed to log interaction: ' + e.message);
      } finally {
        this.addingInteraction = false;
      }
    },
    async deleteInteraction(id) {
      if (!confirm('Delete this interaction?')) return;
      try {
        await api.delete(`v2/customer/${this.id}/cs-notes/interactions/${id}`);
        this.interactions = this.interactions.filter(i => i.id !== id);
      } catch (e) {
        alert('failed to delete: ' + e.message);
      }
    },
    setAddress(index) {
      if (index === 0) {
        this.isSecondary = false;
        this.address = this.customer.address || {};
      } else if (index > 0) {
        this.isSecondary = true;
        if (this.addresses && this.addresses.length >= index) {
          this.address = this.addresses[index - 1];
        } else {
          this.setAddress(0);
        }
      }
    },
    fetchCustomer(id) {
      this.loading = true;
      this.fetchCsNotes(id);
      return api.get(`users/firebase/${id}`)
        .then(({data}) => {
          this.customer = data || {};
          this.customerInitialValues = deepCopy(data);
          const pendingEdits = this.customer.pendingEdits;
          let address = this.customer.address || {};
          if (!address.delivery_instruction && !(pendingEdits && pendingEdits.address && pendingEdits.address.delivery_instruction)) {
            address.delivery_instruction = this.customer.delivery_instruction || '';
          }
          this.address = address || {};

          this.$set(this.customer, 'meal_filters', data.ingredient_preferences ? (data.ingredient_preferences.nutrient_preferences || []) : []);


          this.$set(this.customer, 'useAddress', this.customer.useAddress || {});
          this.$set(this.customer, 'addresses', this.customer.addresses || []);
          this.addresses = this.customer.addresses;

          // add indexes to matching addresses so if they are updated we update useAddress on save
          this.addresses.forEach((a, index) => {
            if (this.customer.useAddress) {
              if (this.customer.useAddress.mon_tue_wed) {
                if (diff({value: this.customer.useAddress.mon_tue_wed}, {value: a}).length === 0) {
                  this.customer.useAddress.mon_tue_wed.index = index;
                }
              }
              if (this.customer.useAddress.thu_fri_sat) {
                if (diff({value: this.customer.useAddress.thu_fri_sat}, {value: a}).length === 0) {
                  this.customer.useAddress.thu_fri_sat.index = index;
                }
              }
            }
            a.index = index;
          });

          this.offerLegacyDeliveryOverrideFix();


          this.subAccounts = {};
          this.setAddress(this.addressTab);
          const uidsToLoad = [
            ...Object.keys((this.customer.subAccounts || {})),
            ...Object.keys((this.customer.accountsManaged || {})),
          ];


          // $set, not plain assignment: this.customer is already reactive, and a plainly-added
          // key has no reactive setter — later v-model writes to it then never invalidate
          // customerSaveValues, so saves report "No changes to save."
          this.$set(this.customer, 'pendingEdits.plan', pendingEdits ? pendingEdits.plan : undefined);
          this.$set(this.customer, 'pendingEdits.allergies', pendingEdits ? pendingEdits.allergies : undefined);
          // the per-restriction settings (avoid it / we'll adjust it) go with the restrictions.
          // No default: an account without the key loads without it, or the save sees a change
          // (`false` is only the pending-edit "clear it" value)
          this.$set(this.customer, 'allergy_settings', this.customer.allergy_settings);
          this.$set(this.customer, 'pendingEdits.allergy_settings', pendingEdits ? pendingEdits.allergy_settings : undefined);
          // the menus the personal menu draws from (the diet pills' Vegan), shown beside the restrictions
          this.$set(this.customer, 'stream_preferences', this.customer.stream_preferences);
          this.$set(this.customer, 'pendingEdits.stream_preferences', pendingEdits ? pendingEdits.stream_preferences : undefined);

          return Promise
            .all(uidsToLoad
              .map(uid => api
                .get(`users/firebase/${uid}`)
                .then(({data}) => this.$set(this.subAccounts, uid, data)))
            )
          // console.log(Object.keys(data.address));
        })
        .catch(e => {
          console.warn('failed to fetch customer ' + id, e);
          this.error = 'Could not find customer.  ' + e;
          this.customer = {};
          this.address = {};
        })
        .finally(() => {
          this.loading = false;
          console.log('customer loaded', this.customer);
        });
    },
    async saveCustomer() {
      this.saving = true;
      if (this.customer.allergies && this.customer.allergies.split) {
        this.customer.allergies = this.customer.allergies.split(',').map(a => a.trim());
      }
      this.customer.addresses = this.addresses;
      console.log('saving', this.customerSaveValues)
      const latestValues = await api.get(`users/firebase/${this.id}`).then(({data}) => data);
      const changesSinceLoad = diff(latestValues, this.customerInitialValues);
      if (changesSinceLoad.length > 0) {
        alert(saveFailedMessage(changesSinceLoad));
        this.saving = false;
        return;
      }
      const rawChangesMade = diff(this.customerInitialValues, this.customerSaveValues);
      const changesMade = rawChangesMade.filter(c => !isNoOpChange(c));
      console.log('changes made', changesMade, '(filtered out', rawChangesMade.length - changesMade.length, 'no-op entries)');
      if (changesMade.length === 0) {
        alert('No changes to save.');
        this.saving = false;
        return;
      }
      const warningProps = ['allergies', 'allergy_settings', 'stream_preferences', 'plan', 'meal_size'];
      const isPendingChange = !!changesMade.find(c => warningProps.includes(c.path[0]));
      if (isPendingChange) {
        if (!confirm('WARNING: you should not edit the plan/allergies/meal_size directly, ' +
          'use pendingEdit.plan and pendingEdit.allergies instead or use incognito mode' +
          '\n\nDO YOU WANT TO IGNORE AND CONTINUE?')) {
          this.saving = false;
          return this.fetchCustomer(this.id);
        }
      }
      this.pendingChanges = changesMade;
      this.showSaveConfirmDialog = true;
      this.saving = false;
    },
    cancelSave() {
      this.showSaveConfirmDialog = false;
      this.pendingChanges = [];
    },
    reloadCustomer() {
      if (this.hasUnsavedChanges && !confirm('You have unsaved changes. Reload and discard them?')) {
        return;
      }
      return this.fetchCustomer(this.id);
    },
    handleBeforeUnload(e) {
      if (this.hasUnsavedChanges) {
        // browsers ignore the custom message and show their own, but a non-empty returnValue triggers the prompt
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    },
    async performSaveCustomer() {
      this.saving = true;
      this.showSaveConfirmDialog = false;
      return api.put(`users/firebase/${this.id}`, this.customerSaveValues)
        .then(({data}) => {
          this.customer = data || {};
          this.fetchCustomer(this.id);
          alert('customer changes saved!');
        })
        .catch(e => {
          // the server's reason first (e.g. a plan change box ↔ meals refused
          // while the billed week still holds an order), else the transport error
          const reason = (e.response && e.response.data && (e.response.data.message || e.response.data.error)) || e.message;
          alert('failed to save changes!  ' + reason);
          console.warn('failed to save customer ' + this.id, e);
          this.error = 'Could not find customer.  ' + e;
          this.customer = {};
          this.address = {};
        })
        .finally(() => {
          this.loading = false;
          this.saving = false;
          this.pendingChanges = [];
        });
    },
    formatChangeValue(value) {
      if (value === null) return 'null';
      if (value === undefined) return '(unset)';
      if (typeof value === 'object') return JSON.stringify(value);
      return String(value);
    },
    changeTypeColor(type) {
      if (type === 'CREATE') return 'green';
      if (type === 'REMOVE') return 'red';
      return 'blue';
    },
    getProperty(field) {
      return field.split(':')[0];
    },
    isCurrency(field) {
      return field.split(':')[1] === 'currency';
    },
    isTime(field) {
      return field.split(':')[1] === 'time';
    },
    isMultiple(field) {
      return field.split(':')[1] === 'options-multiple';
    },
    isCheckbox(field) {
      return field.split(':')[1] === 'boolean';
    },
    getText(field) {
      return field.split(':')[3] || 'text';
    },
    getValue(field) {
      return field.split(':')[4] || 'value';
    },
    isSelect(field) {
      const splitElement = (field.split(':')[1] || '').split('-')[0];
      return splitElement === 'options';
    },
    selectOptions(field) {
      let vals = field.split(':').slice(2);
      vals = vals.join(':');
      const prop = vals.match(/this\./);
      if (prop) {
        return this[vals.replace('this.', '')];
      }
      return vals.split(',');
    },
    isTextField(field) {
      return field.split(':').length === 1;
    },
    // the settings key beside an allergies field: allergies → allergy_settings,
    // pendingEdits.allergies → pendingEdits.allergy_settings
    allergySettingsProperty(field) {
      return this.getProperty(field).replace(/allergies$/, 'allergy_settings');
    },
    // "only from Plant-Based · leaves out Keto" for the stream_preferences beside an
    // allergies field (allergies → stream_preferences, pendingEdits.allergies → pendingEdits.stream_preferences)
    streamPreferenceText(field) {
      const prefs = this.customer[this.getProperty(field).replace(/allergies$/, 'stream_preferences')];
      if (!prefs || typeof prefs !== 'object') return '';
      const names = (ids) => (ids || []).map(id => this.$store.getters.getStreamName(id) || id).join(', ');
      return [
        (prefs.include || []).length ? `only from ${names(prefs.include)}` : '',
        (prefs.exclude || []).length ? `leaves out ${names(prefs.exclude)}` : '',
      ].filter(Boolean).join(' · ');
    },
    isAllergyField(field) {
      return field.split(':')[1] === 'allergy';
    },
    changeEmailPrompt() {
      const email = prompt('enter the new email for this customer');
      this.loading = true;
      if (email) {
        api.post('v2/user/update-email', {
          email,
          uid: this.customer.uid
        })
          .then(r => {
            console.log('result', r);
            const {
              ok,
              error
            } = r.data;
            if (ok) {
              alert('succeeded');
              return this.fetchCustomer(this.customer.uid);
            } else {
              alert('failed to change: ' + error)
            }
          })
          .catch((e) => alert('failed to change: ' + e))
          .finally(() => this.loading = false);
      }
    },
    changePasswordPrompt() {
      const password = prompt('enter the password you would like to set');
      if (password) {
        api.post('v2/user/update-password', {
          email: this.customer.email,
          uid: this.customer.uid,
          password
        })
          .then(r => {
            console.log('result', r);
            const {
              ok,
              error
            } = r.data;
            if (ok) {
              alert('succeeded');
            } else {
              alert('failed to change: ' + error);
            }
          })
          .catch((e) => alert('failed to change: ' + e));
      }
    },
    confirmRemoveManagedAccount(uid) {
      if (confirm('Are you sure you want to remove this managed account?')) {
        return this.updateManagedAccount(uid, false);
      }
    },
    async confirmUnlinkSub(uid) {
      if (confirm('Are you sure you want to disconnect this sub account?  Note, you will need to ensure the sub account has a payment method after disconnecting.')) {
        if (!this.confirmAccountChangeWindow()) {
          return;
        }
        // delete this.customer.subAccounts[uid];
        // delete this.subAccounts[uid];
        // this.$set(this.customer, 'subAccounts', this.customer.subAccounts);
        // return this.saveCustomer();
        const result = await api.post('v2/customer/unlink-sub-account', {
          mainAccountUid: this.id,
          subAccountUid: uid
        });
        this.fetchCustomer(this.id);
        alert('subaccount unlinked changes saved! ' + JSON.stringify(result.data));
      }
    },
    deleteCustomer() {
      if (confirm('Are you sure you want to delete this customer?  No going back!')) {
        return api.delete(`v2/user/${this.id}`)
          .then(({data}) => {
            this.customer = data;
          });
      }
    },
    async updateManagedAccount(uid, allowed) {
      const result = await api.post('v2/customer/update-managed-account', {
        managerUid: this.id,
        uid,
        allowed
      });
      console.log('result', result);
      return this.fetchCustomer(this.id)
    },
    // Sub-account / main-account changes can only be made between Sunday and
    // Wednesday at noon (America/Vancouver). Outside that window the upcoming
    // week's Stripe payments may already have been taken and would need to be
    // fixed manually.
    isAccountChangeWindowOpen() {
      const now = moment.tz('America/Vancouver');
      const day = now.day(); // 0 = Sunday ... 6 = Saturday
      return day < 3 || (day === 3 && now.hour() < 12);
    },
    confirmAccountChangeWindow() {
      if (this.isAccountChangeWindowOpen()) {
        return true;
      }
      const now = moment.tz('America/Vancouver');
      return confirm(
        'Sub-account / main-account settings can only be changed between Sunday and Wednesday at noon (America/Vancouver).\n\n' +
        `It is currently ${now.format('dddd h:mm a')} (America/Vancouver), which is outside that window.\n\n` +
        'WARNING: if any Stripe payments have already been made for the upcoming week (e.g. a new signup), they will need to be fixed manually.\n\n' +
        'Do you still want to continue?'
      );
    },
    async addSubAccount(user) {
      if (user.subAccounts) {
        alert(`the user ${user.email} has subaccounts - they must be removed before you can make ${user.email} a subaccount`);
        return;
      }
      if (!this.confirmAccountChangeWindow()) {
        return;
      }
      this.loading = true;
      const {uid} = user;
      // this.customer.subAccounts = this.customer.subAccounts || {};
      // this.$set(this.customer.subAccounts, uid, {
      //   uid,
      //   timestamp: Date.now()
      // });
      // this.subAccounts[uid] = user;
      // this.loading = false;
      // await this.saveCustomer();
      const result = await api.post('v2/customer/link-sub-account', {
        mainAccountUid: this.id,
        subAccountUid: uid
      });
      this.fetchCustomer(this.id);
      alert('subaccount linked changes saved! ' + JSON.stringify(result.data));
      this.showAddSubDialog = false;
    },
    addDonateDate() {
      if (!this.newDonateDate) return;
      if (!this.customer.donateDates) {
        this.$set(this.customer, 'donateDates', []);
      }
      if (!this.customer.donateDates.includes(this.newDonateDate)) {
        this.customer.donateDates.push(this.newDonateDate);
        this.customer.donateDates = [...this.customer.donateDates].sort();
      }
      this.newDonateDate = null;
    },
    coordinatesState,
    mapsUrl,
    addNewAddress() {
      this.addresses.push({line1: 'new', index: this.addresses.length});
    },
    offerLegacyDeliveryOverrideFix() {
      const useAddress = this.customer.useAddress;
      if (!useAddress) return;

      // Current schema: mon_tue_wed / thu_fri_sat (2 slots).
      // Legacy schema: mon_tue / wed_thu / fri_sat (3 slots). Only mon_tue maps cleanly
      // (mon_tue -> mon_tue_wed). wed_thu / fri_sat are ambiguous — need staff to confirm
      // with the customer how Wednesday should be assigned. Their addresses still get added
      // to `addresses` so they appear in the dropdown for manual re-assignment.
      const hasMonTueLegacy = !!useAddress.mon_tue;
      const ambiguousLegacyKeys = ['wed_thu', 'fri_sat'].filter(k => useAddress[k]);

      const currentSlots = ['mon_tue_wed', 'thu_fri_sat'];
      const orphanCurrentSlots = currentSlots.filter(slot => {
        const override = useAddress[slot];
        if (!override) return false;
        return !this.addresses.some(a => diff({value: a}, {value: override}).length === 0);
      });

      if (!hasMonTueLegacy && ambiguousLegacyKeys.length === 0 && orphanCurrentSlots.length === 0) {
        return;
      }

      const messages = [];
      if (hasMonTueLegacy) {
        messages.push(`- 'mon_tue' will be migrated to 'mon_tue_wed' and added to 'addresses'`);
      }
      if (ambiguousLegacyKeys.length) {
        messages.push(
          `- legacy override(s) ${ambiguousLegacyKeys.join(', ')} cannot be auto-migrated to the current ` +
          `mon_tue_wed / thu_fri_sat schema. Address(es) will be added to 'addresses' but you'll need to ` +
          `confirm with the customer whether the Wednesday delivery should use a different address, then ` +
          `re-assign the overrides manually on the Delivery tab.`
        );
      }
      if (orphanCurrentSlots.length) {
        messages.push(`- override address(es) for ${orphanCurrentSlots.join(', ')} will be added to 'addresses'`);
      }
      const ok = confirm(
        `This customer has delivery override data that needs migration:\n${messages.join('\n')}\n\n` +
        `Apply the changes above? (You'll still need to click Save afterwards.)`
      );
      if (!ok) return;

      const ensureInAddresses = (override) => {
        let match = this.addresses.find(a => diff({value: a}, {value: override}).length === 0);
        if (!match) {
          const newIndex = this.addresses.length;
          match = {...override, index: newIndex};
          this.addresses.push(match);
        }
        return match;
      };

      // mon_tue -> mon_tue_wed (only if mon_tue_wed not already set to something different).
      if (hasMonTueLegacy) {
        if (!useAddress.mon_tue_wed) {
          this.$set(useAddress, 'mon_tue_wed', useAddress.mon_tue);
        }
        this.$delete(useAddress, 'mon_tue');
      }

      // Always ensure every override address (legacy or current) is in `addresses`.
      // For current-schema slots, also re-link the slot to the entry in `addresses`.
      for (const slot of [...currentSlots, ...ambiguousLegacyKeys]) {
        const override = useAddress[slot];
        if (!override) continue;
        const match = ensureInAddresses(override);
        if (currentSlots.includes(slot)) {
          this.$set(useAddress, slot, match);
        }
      }
    },
    addNote() {
      if (!this.customer.customer_notes) {
        this.$set(this.customer, 'customer_notes', []);
      }
      this.customer.customer_notes.unshift({start: '', end: '', text: ''})
    },
    async fetchMealFilters() {
      await api.get('/v2/meal-filter')
        .then(({data}) => {
          this.availableFilters = data;
          this.availableFilterNames = data.map(d => d.id);
        });
    },
    hasPendingEdit(field) {
      const property = this.getProperty(field);
      if (!this.customer.pendingEdits || this.customer.pendingEdits[property] === undefined) {
        return false;
      }

      const currentValue = this.customer[property];
      const pendingValue = this.customer.pendingEdits[property];

      // Use microdiff to compare values (handles arrays and objects properly)
      return diff({value: currentValue}, {value: pendingValue}).length > 0;
    },
    formatPendingEdit(field) {
      const property = this.getProperty(field);
      const pendingValue = this.customer.pendingEdits[property];

      if (Array.isArray(pendingValue)) {
        return pendingValue.join(', ');
      }

      return pendingValue;
    },
    /**
     * the addresses an account delivers to, each once: its address and day-of-week
     * overrides as they are now, then as a pending edit will make them
     */
    deliveryAddresses(account) {
      const slotLabels = {mon_tue_wed: 'Mon-Wed', thu_fri_sat: 'Thu-Sat', mon_tue: 'Mon-Tue', wed_thu: 'Wed-Thu', fri_sat: 'Fri-Sat'};
      const addressesOf = (address, useAddress) => {
        const overrides = Object.entries(useAddress || {})
          .filter(([slot, a]) => slotLabels[slot] && a && a.line1)
          .map(([slot, a]) => ({address: a, slot: slotLabels[slot]}));
        // with both halves of the week overridden, the plain address gets no deliveries
        const coversWeek = overrides.length >= 2;
        return [
          ...(address && address.line1 && !coversWeek ? [{address, slot: ''}] : []),
          ...overrides
        ];
      };
      const pendingEdits = account.pendingEdits || {};
      const current = addressesOf(account.address, account.useAddress);
      const pending = (pendingEdits.address || pendingEdits.useAddress)
        ? addressesOf(pendingEdits.address || account.address, pendingEdits.useAddress || account.useAddress)
        : [];
      const seen = new Set();
      const result = [];
      const add = ({address, slot}, isPending) => {
        const key = (formatAddress(address) + '|' + (address.location || '')).toUpperCase();
        if (seen.has(key)) {
          return;
        }
        seen.add(key);
        const note = isPending ? ['pending edit', slot].filter(n => n).join(', ') : slot;
        result.push({address, note, pending: isPending});
      };
      current.forEach(a => add(a, false));
      pending.forEach(a => add(a, true));
      return result;
    },
    hasAddressPendingEdit(field) {
      const property = this.getProperty(field);
      const addressIndex = this.isSecondary ? this.addressTab - 1 : 0;
      const pendingEditsPath = this.isSecondary
        ? `addresses.${addressIndex}.${property}`
        : `address.${property}`;

      if (!this.customer.pendingEdits) {
        return false;
      }

      // Check for nested pending edits like address.city or addresses.0.city
      let pendingValue = this.getNestedProperty(this.customer.pendingEdits, pendingEditsPath);

      // annoying field can be in either place
      if (!pendingValue && field === 'delivery_instruction') {
        pendingValue = this.customer.pendingEdits.delivery_instruction;
      }

      if (pendingValue === undefined) {
        return false;
      }

      const currentValue = this.address[property];

      // Use microdiff to compare values (handles arrays and objects properly)
      return diff({value: currentValue}, {value: pendingValue}).length > 0;
      // return diff(currentValue, pendingValue).length > 0;
    },
    formatAddressPendingEdit(field) {
      const property = this.getProperty(field);
      const addressIndex = this.isSecondary ? this.addressTab - 1 : 0;
      const pendingEditsPath = this.isSecondary
        ? `addresses.${addressIndex}.${property}`
        : `address.${property}`;

      const pendingValue = this.getNestedProperty(this.customer.pendingEdits, pendingEditsPath);

      if (Array.isArray(pendingValue)) {
        return pendingValue.join(', ');
      }

      return pendingValue;
    },
    getNestedProperty(obj, path) {
      return path.split('.').reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : undefined;
      }, obj);
    },
    dateFormatted,
  },
  computed: {
    ...mapState(['plans', 'shipping', 'user']),
    /**
     * this account and its subaccounts, grouped into deliveries the way the Shipments
     * page groups them (same door, overlapping delivery windows). an account goes under
     * every address it delivers to: its address, any day-of-week override, and a pending
     * edit's address, so an account moving house shows at both
     */
    subAccountGroups() {
      const uids = [this.customer.uid || this.id, ...Object.keys(this.customer.subAccounts || {})];
      const entries = [];
      const notFound = [];
      for (const uid of uids) {
        const isMain = uid === uids[0];
        const account = isMain ? this.customer : this.subAccounts[uid];
        const addresses = account ? this.deliveryAddresses(account) : [];
        if (!addresses.length) {
          notFound.push({uid, account, isMain, note: account ? 'no address' : ''});
        }
        addresses.forEach(({address, note, pending}) => entries.push({uid, account, isMain, address, note, pending}));
      }
      const groups = Object.entries(groupOrdersByDelivery(entries))
        .map(([address, members]) => ({address, members}));
      if (notFound.length) {
        groups.push({address: 'No address', members: notFound});
      }
      return groups;
    },
    // The website this customer uses, as the web app records it on the
    // account (users/<uid>.ui_version; 'legacy' is the old name of
    // 'personalized'), and the link that opens the customer there.
    // Personalized Fed on its own domain, so the admin's browser on
    // app.fedfedfed.com keeps its own setting; Latest Fed has no domain of
    // its own, so that link names the UI (?fed-ui=v2). null = no record yet
    customerSite() {
      const sites = {
        v2: {label: 'V4', href: `https://app.fedfedfed.com/?uid=${this.id}&fed-ui=v2`},
        personalized: {label: 'V3', href: `https://personalized.fedfedfed.com/?uid=${this.id}&fed-ui=personalized`},
        v1: {label: 'V2', href: `https://app.fedfedfed.com/v1/profile?uid=${this.id}`},
      };
      const version = this.customer && this.customer.ui_version;
      return sites[version === 'legacy' ? 'personalized' : version] || null;
    },
    csGeneralNoteDirty() {
      const saved = (this.csGeneralNote && this.csGeneralNote.note) || '';
      return (this.csGeneralNoteDraft || '') !== saved;
    },
    hasUnsavedChanges() {
      if (!this.customerInitialValues || !this.customer || Object.keys(this.customerInitialValues).length === 0) {
        return false;
      }
      try {
        const changes = diff(this.customerInitialValues, this.customerSaveValues).filter(c => !isNoOpChange(c));
        return changes.length > 0;
      } catch (e) {
        return false;
      }
    },
    orderDate() {
      return getNextOrderingDate();
    },
    prevOrderDate() {
      return moment(getNextOrderingDate()).subtract(1, 'weeks').format('YYYY-MM-DD');
    },
    createdDate() {
      const customer = this.customer
      const date = customer.account_created || (customer.stripe && customer.stripe.created_at)
      return date ? dateFormatted(date, {formatString: 'LLL'}) : ''
    },
    addressList() {
      return this.addresses ? this.addresses.map((a) => (`[${a.index + 1}]`) + formatAddress(a, {short: true})) : [];
    },
    addressFields() {
      const cities = Object.keys(this.shipping);
      return [
        "buzzer",
        "unit", "line1", "line2",
        'blank',
        `city`,
        `city:options:${cities.join(',')}`,
        'state:options:BC,ON,AB,SK,MB,NT,NB,NL,NS,PE,QC,NU,YT',
        "postal_code",
        "location:options:home,office",
        "earliest_time:time",
        "latest_time:time",
        // "delivery_instruction",
        'use_shipping:boolean'
        // "country", "state"
      ]
    },

    fields() {
      return [
        "first_name",
        "last_name",
        `plan:options:${Object.keys(this.plans).join(',')}`,
        ...(this.canEditPlan ? [`pendingEdits.plan:options:${Object.keys(this.plans).join(',')}`] : []),
        "meal_size:options:small,medium,large",
        "phone_number",
        // "delivery_instruction",
        `allergies:allergy`,
        `pendingEdits.allergies:allergy`,
        `meal_filters:options-multiple:this.availableFilterNames`,
        "tags:options-multiple:DoNotPrint",
        "price_adjustment:currency",
        "price_adjustment_label",
        // "free_shipping:boolean",
        "meal_at_door:boolean",
        // "newsletter:boolean",
        // "show_meals:boolean",
        "allow_allergy_swaps:boolean",
        // "is_active:boolean",
        "login_disabled:boolean",
        "last_bin_check:boolean",
        "newsletter:boolean",
        "weeklyReminders:boolean",
        // "failed_payment_excempt:boolean",
        ...(this.canChangeAdmin
            ? [
              "disable_auto_charge:boolean",
              "is_admin:boolean",
              "is_subaccount_admin:boolean"
            ]
            : []
        ),
        ...(this.canEditCommission
            ? [
              "is_account_manager:boolean",
              "commission_percent:options:3,4,5,6"
            ]
            : []
        )
      ];
    },
    upcomingDeliveryDates() {
      const dates = [];
      const now = moment();
      const date = moment();
      let count = 4;
      while (count-- > 0) {
        date.startOf('week');
        if (date.isSameOrAfter(now, 'day')) {
          dates.push(date.format(moment.HTML5_FMT.DATE));
        }
        date.add(3, 'days');
        if (date.isSameOrAfter(now, 'day')) {
          dates.push(date.format(moment.HTML5_FMT.DATE));
        }
        date.add(1, 'week');
      }
      return dates;
    },
    customerSaveValues() {
      const customer = {...this.customer};
      customer.first_name = normalizeName(customer.first_name);
      customer.last_name = normalizeName(customer.last_name);
      if (customer.meal_filters) {
        customer.ingredient_preferences = customer.ingredient_preferences || {};
        customer.ingredient_preferences.nutrient_preferences = this.customer.meal_filters;
        delete customer.meal_filters;
      }

      if (customer.useAddress) {
        if (customer.useAddress.mon_tue_wed) {
          const index = customer.addresses.findIndex(a => a.index === customer.useAddress.mon_tue_wed.index);
          if (index === -1) {
            alert('could not set mon_tue_wed delivery overrride');
          } else {
            customer.useAddress.mon_tue_wed = this.addresses[index];
          }
        }
        if (customer.useAddress.thu_fri_sat) {
          const index = customer.addresses.findIndex(a => a.index === customer.useAddress.thu_fri_sat.index);
          if (index === -1) {
            alert('could not set thu_fri_sat delivery overrride');
          } else {
            customer.useAddress.thu_fri_sat = this.addresses[index];
          }
        }
      }

      // replaces things like A.B.C = 1 with A : { B : { C : 1 } } }
      dotPathToObject(customer);
      return customer;
    }
  },
}
</script>

<style scoped>

</style>
